// Retry utilities for handling transient failures

export interface RetryOptions {
  maxRetries?: number;
  baseDelay?: number;
  maxDelay?: number;
  backoffFactor?: number;
  retryCondition?: (error: any) => boolean;
}

const defaultRetryCondition = (error: any): boolean => {
  if (!error) return false;

  // Check for common retryable error conditions
  const retryableErrors = [
    'network',
    'timeout',
    'connection',
    'temporary',
    'rate limit',
    'server error',
    '503',
    '502',
    '504',
    'ETIMEDOUT',
    'ECONNRESET',
    'ENOTFOUND',
  ];

  const errorMessage = error.message?.toLowerCase() || '';
  const errorCode = error.code?.toString() || '';

  return retryableErrors.some(
    (pattern: string) =>
      errorMessage.includes(pattern) || errorCode.includes(pattern)
  );
};

/**
 * Simple retry utility for any async operation
 * @param operation - The async function to retry
 * @param maxRetries - Maximum number of retry attempts (default: 3)
 * @returns Promise that resolves with the operation result
 */
export const withRetry = async <T>(
  operation: () => Promise<T>,
  maxRetries = 3
): Promise<T> => {
  let lastError: any;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      if (attempt > 0) {
        console.log(
          `Retrying operation, attempt ${attempt + 1}/${maxRetries + 1}`
        );
        // Wait before retrying with exponential backoff
        const delay = Math.min(Math.pow(2, attempt) * 1000, 10000); // Cap at 10 seconds
        await new Promise((resolve) => setTimeout(resolve, delay));
      }

      const result = await operation();

      if (attempt > 0) {
        console.log(`Operation succeeded on attempt ${attempt + 1}`);
      }

      return result;
    } catch (error: any) {
      lastError = error;

      // Check if this error should trigger a retry
      const shouldRetry = defaultRetryCondition(error);

      if (attempt === maxRetries || !shouldRetry) {
        console.error(`Operation failed after ${attempt + 1} attempts:`, error);
        throw error;
      }

      console.warn(
        `Operation attempt ${attempt + 1} failed, will retry:`,
        error
      );
    }
  }

  throw lastError;
};

/**
 * Advanced retry utility with customizable options
 * @param operation - The async function to retry
 * @param options - Retry configuration options
 * @returns Promise that resolves with the operation result
 */
export const withAdvancedRetry = async <T>(
  operation: () => Promise<T>,
  options: RetryOptions = {}
): Promise<T> => {
  const {
    maxRetries = 3,
    baseDelay = 1000,
    maxDelay = 30000,
    backoffFactor = 2,
    retryCondition = defaultRetryCondition,
  } = options;

  let lastError: any;
  let currentDelay = baseDelay;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      if (attempt > 0) {
        console.log(
          `Retrying operation, attempt ${attempt + 1}/${maxRetries + 1}`
        );
        // Wait before retrying
        await new Promise((resolve) => setTimeout(resolve, currentDelay));
      }

      const result = await operation();

      if (attempt > 0) {
        console.log(`Operation succeeded on attempt ${attempt + 1}`);
      }

      return result;
    } catch (error: any) {
      lastError = error;

      // Check if this error should trigger a retry
      const shouldRetry = retryCondition(error);

      if (attempt === maxRetries || !shouldRetry) {
        console.error(`Operation failed after ${attempt + 1} attempts:`, error);
        throw error;
      }

      console.warn(
        `Operation attempt ${
          attempt + 1
        } failed, will retry in ${currentDelay}ms:`,
        error
      );

      // Calculate next delay with exponential backoff, capped at maxDelay
      currentDelay = Math.min(currentDelay * backoffFactor, maxDelay);
    }
  }

  throw lastError;
};

/**
 * Retry utility specifically for Supabase RPC calls
 * @param rpcCall - The Supabase RPC call function
 * @param maxRetries - Maximum number of retry attempts (default: 3)
 * @returns Promise that resolves with the RPC result
 */
export const withSupabaseRetry = async (
  rpcCall: () => Promise<any>,
  maxRetries = 3
): Promise<any> => {
  return withRetry(rpcCall, maxRetries);
};

/**
 * Retry utility for database operations that might fail due to transient issues
 * @param dbOperation - The database operation function
 * @param options - Retry configuration options
 * @returns Promise that resolves with the operation result
 */
export const withDatabaseRetry = async <T>(
  dbOperation: () => Promise<T>,
  options: RetryOptions = {}
): Promise<T> => {
  return withAdvancedRetry(dbOperation, {
    maxRetries: 2,
    baseDelay: 500,
    maxDelay: 5000,
    backoffFactor: 2,
    ...options,
  });
};
